import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MAX_RULES = 6;
const MIN_RULES = 1;
const MAX_RULE_CHARS = 200;
const MAX_CONTENT = 2;
const MIN_CONTENT = 1;
const MAX_CONTENT_CHARS = 200;
const MIN_POINTS = 1;
const MAX_POINTS = 100;
const MIN_QUESTIONS = 1;
const MAX_QUESTIONS = 100;
const MAX_THANKYOU_CHARS = 300;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

function isThemeFlagOn(val, defaultOn = false) {
  if (val == null || val === "") return defaultOn;
  return String(val).toLowerCase() === "true";
}

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const normalizeCountInput = (raw) => String(raw).replace(/\D/g, "");

function parseStringList(raw) {
  if (Array.isArray(raw)) return raw.map((r) => String(r ?? ""));
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((r) => String(r ?? ""));
    } catch {
      /* not JSON */
    }
  }
  return [];
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([""]);
  const [gameContent, setGameContent] = useState([""]);
  const [thankYouText, setThankYouText] = useState("");
  const [correctPoints, setCorrectPoints] = useState(String(MIN_POINTS));
  const [noOfQuestion, setNoOfQuestion] = useState(String(MIN_QUESTIONS));
  const [shuffle, setShuffle] = useState(false);
  const [revealAnswer, setRevealAnswer] = useState(false);
  const [boxTextColor, setBoxTextColor] = useState("#000000");
  const [buttonBoxColor, setButtonBoxColor] = useState("#fbf99f");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    const loadedRules = parseStringList(data.rules);
    setRules(loadedRules.length ? loadedRules : [""]);
    const loadedContent = parseStringList(data.game_content);
    setGameContent(loadedContent.length ? loadedContent : [""]);
    setThankYouText(String(data.custom_text_thank_you_page ?? "").slice(0, MAX_THANKYOU_CHARS));
    setCorrectPoints(String(parseInt(data.correctPoints ?? data.points, 10) || MIN_POINTS));
    setNoOfQuestion(String(parseInt(data.noOfQuestion, 10) || MIN_QUESTIONS));
    setShuffle(isThemeFlagOn(data.shuffle, false));
    setRevealAnswer(isThemeFlagOn(data.question_table, false));
    setBoxTextColor(toColorInputValue(data.landing_page_title_color));
    setButtonBoxColor(toColorInputValue(data.questionBox_color || "#fbf99f"));
    setButtonTextColor(toColorInputValue(data.button_Textcolor || "#ffffff"));
  }, [data]);

  const updateRule = (index, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? value : rule)));
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Error", `Maximum ${MAX_RULES} rules allowed`, "error");
      return;
    }
    setRules((prev) => [...prev, ""]);
  };

  const removeRule = (index) => {
    if (rules.length <= MIN_RULES) {
      Swal.fire("Error", "At least 1 rule is required", "error");
      return;
    }
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const updateContent = (index, value) => {
    setGameContent((prev) => prev.map((c, i) => (i === index ? value : c)));
  };

  const addContent = () => {
    if (gameContent.length >= MAX_CONTENT) {
      Swal.fire("Error", `Maximum ${MAX_CONTENT} content entries allowed`, "error");
      return;
    }
    setGameContent((prev) => [...prev, ""]);
  };

  const removeContent = (index) => {
    if (gameContent.length <= MIN_CONTENT) {
      Swal.fire("Error", "At least 1 game content entry is required", "error");
      return;
    }
    setGameContent((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const trimmed = rules.map((r) => String(r ?? "").trim());
    const filled = trimmed.filter(Boolean);
    const trimmedContent = gameContent.map((c) => String(c ?? "").trim());
    const filledContent = trimmedContent.filter(Boolean);
    const errors = [];

    if (!filled.length) {
      errors.push("At least 1 rule is required");
    }
    const longRules = trimmed
      .map((text, i) => (text.length > MAX_RULE_CHARS ? i + 1 : null))
      .filter(Boolean);
    if (longRules.length) {
      errors.push(`Rules ${longRules.join(", ")} exceed ${MAX_RULE_CHARS} characters`);
    }
    if (rules.length > MAX_RULES) {
      errors.push(`Maximum ${MAX_RULES} rules allowed`);
    }
    if (!filledContent.length) {
      errors.push("At least 1 game content entry is required");
    }
    const longContent = trimmedContent
      .map((text, i) => (text.length > MAX_CONTENT_CHARS ? i + 1 : null))
      .filter(Boolean);
    if (longContent.length) {
      errors.push(`Game content ${longContent.join(", ")} exceed ${MAX_CONTENT_CHARS} characters`);
    }
    if (gameContent.length > MAX_CONTENT) {
      errors.push(`Maximum ${MAX_CONTENT} game content entries allowed`);
    }
    if (thankYouText.trim().length > MAX_THANKYOU_CHARS) {
      errors.push(`Thank you page text exceeds ${MAX_THANKYOU_CHARS} characters`);
    }
    if (parseInt(correctPoints, 10) < MIN_POINTS || correctPoints === "") {
      errors.push("You must set at least 1 correct answer point.");
    }
    if (parseInt(correctPoints, 10) > MAX_POINTS) {
      errors.push(`Correct answer points cannot exceed ${MAX_POINTS}.`);
    }
    if (parseInt(noOfQuestion, 10) < MIN_QUESTIONS || noOfQuestion === "") {
      errors.push("You must upload at least 1 question.");
    }
    if (parseInt(noOfQuestion, 10) > MAX_QUESTIONS) {
      errors.push(`Number of questions cannot exceed ${MAX_QUESTIONS}.`);
    }

    if (errors.length) return { ok: false, msg: errors.join("\n") };

    const payload = {
      landing_page_title_color: boxTextColor,
      questionBox_color: buttonBoxColor,
      button_Textcolor: buttonTextColor,
      points: parseInt(correctPoints, 10),
      noOfQuestion: parseInt(noOfQuestion, 10),
      shuffle: shuffle ? "true" : "false",
      question_table: revealAnswer ? "true" : "false",
      custom_text_thank_you_page: thankYouText.trim(),
      rules: filled,
      game_content: filledContent,
    };

    return { ok: true, payload, filled };
  };

  const handleSave = async ({ goNext = false } = {}) => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data Updated", "success");
      if (goNext) navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const switchProps = useMemo(
    () => ({
      onColor: buttonBoxColor || "#e25569",
      offColor: "#cbd5e1",
      onHandleColor: "#ffffff",
      offHandleColor: "#ffffff",
      height: 22,
      width: 48,
      handleDiameter: 18,
      uncheckedIcon: false,
      checkedIcon: false,
    }),
    [buttonBoxColor]
  );

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rules-shell">
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="rules-page-header">
        <h1 className="rules-page-header-title">Rules &amp; Game Settings</h1>
        <p className="rules-page-header-desc">
          Configure what players read, how the game scores and shuffles, and the colors used
          throughout this theme.
        </p>
      </div>

      <div className="rules-main-grid">
        <section className="rules-card" data-tour="tour-theme-settings">
          <header className="rules-card-header">
            <span className="rules-card-icon">
              <i className="fa-solid fa-align-left" aria-hidden />
            </span>
            <div>
              <h2 className="rules-card-title">Rules &amp; Messaging</h2>
              <p className="rules-card-subtitle">What players read before, and after, the game</p>
            </div>
          </header>

          <div className="rules-card-body">
            <div className="rules-field-group" data-tour="tour-custom-rules">
              <label className="rules-field-label">Game Rules</label>
              <div className="rules-repeater">
                {rules.map((rule, index) => {
                  const charCount = String(rule ?? "").length;
                  const overLimit = charCount > MAX_RULE_CHARS;
                  return (
                    <div className="rules-repeater-row" key={`rule-${index}`}>
                      <span className="rules-repeater-index">{index + 1}</span>
                      <div className="rules-repeater-field">
                        <textarea
                          name="rules"
                          className="rules-repeater-textarea"
                          placeholder="Rules"
                          value={rule}
                          onChange={(e) => updateRule(index, e.target.value)}
                        />
                        <p className={`rules-char-count${overLimit ? " is-over" : ""}`}>
                          {charCount}/{MAX_RULE_CHARS}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rules-remove-btn"
                        onClick={() => removeRule(index)}
                        disabled={rules.length <= MIN_RULES || loading}
                        aria-label="Remove rule"
                        title="Remove rule"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="rules-add-btn"
                onClick={addRule}
                disabled={rules.length >= MAX_RULES || loading}
              >
                <i className="fa-solid fa-plus" aria-hidden /> Add new rule
              </button>
              <p className="rules-field-hint">
                <code>Up to {MAX_RULES} rules</code>
                <code>At least {MIN_RULES} required</code>
                <code>Max {MAX_RULE_CHARS} characters each</code>
              </p>
            </div>

            <div className="rules-field-group">
              <label className="rules-field-label">Game Page Content</label>
              <div className="rules-repeater">
                {gameContent.map((content, index) => {
                  const charCount = String(content ?? "").length;
                  const overLimit = charCount > MAX_CONTENT_CHARS;
                  return (
                    <div className="rules-repeater-row" key={`content-${index}`}>
                      <span className="rules-repeater-index">{index + 1}</span>
                      <div className="rules-repeater-field">
                        <textarea
                          name="game_content"
                          className="rules-repeater-textarea"
                          placeholder="Game content"
                          value={content}
                          onChange={(e) => updateContent(index, e.target.value)}
                        />
                        <p className={`rules-char-count${overLimit ? " is-over" : ""}`}>
                          {charCount}/{MAX_CONTENT_CHARS}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rules-remove-btn"
                        onClick={() => removeContent(index)}
                        disabled={gameContent.length <= MIN_CONTENT || loading}
                        aria-label="Remove content"
                        title="Remove content"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="rules-add-btn"
                onClick={addContent}
                disabled={gameContent.length >= MAX_CONTENT || loading}
              >
                <i className="fa-solid fa-plus" aria-hidden /> Add Content
              </button>
              <p className="rules-field-hint">
                <code>Up to {MAX_CONTENT} entries</code>
                <code>At least {MIN_CONTENT} required</code>
                <code>Max {MAX_CONTENT_CHARS} characters each</code>
              </p>
            </div>

            <div className="rules-field-group">
              <label className="rules-field-label" htmlFor="rules-thankyou-text">
                Thank You Page Text
              </label>
              <input
                id="rules-thankyou-text"
                type="text"
                className="rules-input"
                maxLength={MAX_THANKYOU_CHARS}
                value={thankYouText}
                onChange={(e) => setThankYouText(e.target.value)}
              />
              <p className="rules-field-hint">
                <code>Shown on the thank you page.</code>
                <code>Max {MAX_THANKYOU_CHARS} characters.</code>
              </p>
            </div>
          </div>
        </section>

        <div className="rules-stack" data-tour="tour-page-content">
          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-sliders" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Gameplay</h2>
                <p className="rules-card-subtitle">Scoring and question behavior</p>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-setting-row">
                <label htmlFor="rules-correct-points" className="rules-setting-label">
                  Correct Answer Points
                </label>
                <input
                  id="rules-correct-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="rules-setting-input"
                  value={correctPoints}
                  onChange={(e) => setCorrectPoints(normalizeCountInput(e.target.value))}
                  onBlur={() => {
                    const n = parseInt(correctPoints, 10);
                    if (correctPoints === "" || Number.isNaN(n) || n < MIN_POINTS) {
                      setCorrectPoints(String(MIN_POINTS));
                    } else if (n > MAX_POINTS) {
                      setCorrectPoints(String(MAX_POINTS));
                    }
                  }}
                />
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-no-of-question" className="rules-setting-label">
                  No of questions to display
                </label>
                <input
                  id="rules-no-of-question"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="rules-setting-input"
                  value={noOfQuestion}
                  onChange={(e) => setNoOfQuestion(normalizeCountInput(e.target.value))}
                  onBlur={() => {
                    const n = parseInt(noOfQuestion, 10);
                    if (noOfQuestion === "" || Number.isNaN(n) || n < MIN_QUESTIONS) {
                      setNoOfQuestion(String(MIN_QUESTIONS));
                    } else if (n > MAX_QUESTIONS) {
                      setNoOfQuestion(String(MAX_QUESTIONS));
                    }
                  }}
                />
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-shuffle" className="rules-setting-label">
                  Shuffle Questions
                </label>
                <Switch
                  id="rules-shuffle"
                  checked={shuffle}
                  onChange={setShuffle}
                  disabled={loading}
                  {...switchProps}
                />
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-reveal-answer" className="rules-setting-label">
                  Reveal the Answer
                </label>
                <Switch
                  id="rules-reveal-answer"
                  checked={revealAnswer}
                  onChange={setRevealAnswer}
                  disabled={loading}
                  {...switchProps}
                />
              </div>
            </div>
          </section>

          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-palette" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Appearance</h2>
                <p className="rules-card-subtitle">Colors used across rules &amp; game screens</p>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-color-grid">
                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: boxTextColor }}
                  >
                    <input
                      type="color"
                      value={boxTextColor}
                      onChange={(e) => setBoxTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Text Color</span>
                </label>

                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: buttonBoxColor }}
                  >
                    <input
                      type="color"
                      value={buttonBoxColor}
                      onChange={(e) => setButtonBoxColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Button Color</span>
                </label>

                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: buttonTextColor }}
                  >
                    <input
                      type="color"
                      value={buttonTextColor}
                      onChange={(e) => setButtonTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Button Text</span>
                </label>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="rules-sticky-actions" data-tour="tour-actions">
        <button
          type="button"
          className="save-and-continue"
          onClick={() => handleSave({ goNext: true })}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default Rules;
